import {EventEmitter, Injectable} from '@angular/core';
import {ActivityCreateCommand} from './activity.create.command';
import {GraphHelperService} from '../../diagram-render/helpers/graph-helper.service';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {ShapeSpawnerService} from '../../diagram-render/shapes/shape-spawner.service';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {DiagramUpdateService} from '../../diagram-data/diagram-update.service';
import {ActivityDeleteCommand} from './activity.delete.command';
import {ActivityChangeStyleCommand} from './activity.changestyle.command';
import {AngularComponentService} from '../../diagram-render/mx-graph/graph/angular-component.service';
import {ActivityAddAttachmentCommand} from './activity.addattachment.command';
import {ActivityAddUrlRecordCommand} from './activity.addurlrecord.command';
import {ActivityConvertAttachmentCommand} from './activity.convertattachment.command';
import {ActivityChangeAttachmentCommand} from './activity.changeattachment.command';
import {ActivityDeleteAttachmentCommand} from './activity.deleteattachment.command';
import {ActivityChangeImageCommand} from './activity.changeimage.command';
import {ActivityAddRequirementsCommand} from './activity.addrequirements.command';
import {Q9IntercomService} from '../../core/services/q9-intercom.service';
import {ActivityAddStoryCommand} from './activity.addstory.command';
import {ActivityAddFooterTextCommand} from './activity.addFooterText.command';
import {ActivityChangeFooterTextCommand} from './activity.changeFooterText.command';
import {ActivityChangeContainerCommand} from './activity.changeContainer.command';
import {ActivityAddAttributeCommand} from './activity.addAttribute.command';
import {ActivityChangeAttributeCommand} from './activity.changeAttribute.command';
import {ActivityDeleteAttributeCommand} from './activity.deleteAttribute.command';
import {ActivityReorderCommand} from './activity.reorder.command';
import {ActivitySwapAttributeCommand} from './activity.swapAttribute.command';
import {ActivityChangeAttributeDescriptionCommand} from './activity.changeAttributeDescription.command';
import {ActivityChangeAttributePaddingCommand} from './activity.changeAttributePadding.command';
import {ActivityAddOrgModelNodesAttachmentCommand} from './actvity.addorgmodelnodesattachment.command';
import {ActivityDeleteOrgModelNodesAttachmentCommand} from './actvity.deleteOrgModelNodesAttachment.command';
import {OrgModelNodesAttachment} from 'src/app/shared/models/org-model-nodes-attachment.interface';
import {SanitizeXssService} from '../../core/services/santize-xss.service';
import {ActivityRemoveLinkedDiagramCommand} from './activity.removeLinkedDiagram.command';
import {ActivityAddLinkedDiagramCommand} from './activity.addLinkedDiagram.command';
import {FlowlineHelperService} from '../../diagram-render/helpers/flowline-helper.service';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';
import {ActivityRenumberCommand} from './activity.renumber.command';

@Injectable()
export class ActivityCommands {
  private serviceObj: object;

  constructor(
    graphHelperSvc: GraphHelperService,
    jsonHandlerSvc: DiagramJsonHandlerService,
    shapeSpawnerSvc: ShapeSpawnerService,
    editorRegistrySvc: EditorRegistryService,
    flHelper: FlowlineHelperService,
    diagramUpdateSvc: DiagramUpdateService,
    angularCompSvc: AngularComponentService,
    intercom: Q9IntercomService,
    sanitizeXssService: SanitizeXssService,
    diagramTypeConfigService: DiagramTypeConfigService
  ) {
    this.serviceObj = {
      graphHelperSvc,
      jsonHandlerSvc,
      shapeSpawnerSvc,
      editorRegistrySvc,
      diagramUpdateSvc,
      angularCompSvc,
      intercom,
      flHelper,
      sanitizeXssService,
      diagramTypeConfigService
    };
  }

  public create(data) {
    return new ActivityCreateCommand(data).injectServices(this.serviceObj);
  }

  public delete(data) {
    return new ActivityDeleteCommand(data).injectServices(this.serviceObj);
  }

  public changeStyle(mxKey: number, style: any, customStyle: boolean) {
    return new ActivityChangeStyleCommand({mxKey, style, customStyle})
      .injectServices(this.serviceObj)
      .prepareData();
  }

  public changeImage(change) {
    return new ActivityChangeImageCommand(change).injectServices(this.serviceObj).prepareData();
  }

  public addAttachment(data, cb?: EventEmitter<boolean>) {
    let command = new ActivityAddAttachmentCommand(data).injectServices(this.serviceObj);

    if (cb) {
      command = command.injectCallBackEmitter(cb);
    }

    return command;
  }

  public addUrlRecord(data) {
    return new ActivityAddUrlRecordCommand(data).injectServices(this.serviceObj);
  }

  public convertAttachment(data) {
    return new ActivityConvertAttachmentCommand(data).injectServices(this.serviceObj).prepareData();
  }

  public changeAttachment(data, cb?: EventEmitter<boolean>) {
    return new ActivityChangeAttachmentCommand(data)
      .injectServices(this.serviceObj)
      .prepareData(cb);
  }

  public deleteAttachment(data) {
    return new ActivityDeleteAttachmentCommand(data).injectServices(this.serviceObj);
  }

  public addRequirements(data) {
    return new ActivityAddRequirementsCommand(data).injectServices(this.serviceObj);
  }

  public addStories(data) {
    return new ActivityAddStoryCommand(data).injectServices(this.serviceObj);
  }

  public addAttribute(data) {
    return new ActivityAddAttributeCommand(data).injectServices(this.serviceObj);
  }

  public changeAttribute(data) {
    return new ActivityChangeAttributeCommand(data).injectServices(this.serviceObj).prepareData();
  }

  public deleteAttribute(data) {
    return new ActivityDeleteAttributeCommand(data).injectServices(this.serviceObj).prepareData();
  }

  public swapAttribute(data) {
    return new ActivitySwapAttributeCommand(data).injectServices(this.serviceObj).prepareData();
  }

  public changeAttributeDescription(data) {
    return new ActivityChangeAttributeDescriptionCommand(data)
      .injectServices(this.serviceObj)
      .prepareData();
  }

  public addFooterText(data) {
    return new ActivityAddFooterTextCommand(data).injectServices(this.serviceObj);
  }

  public changeFooterText(data) {
    return new ActivityChangeFooterTextCommand(data).injectServices(this.serviceObj).prepareData();
  }

  public changeContainer(data) {
    return new ActivityChangeContainerCommand(data).injectServices(this.serviceObj);
  }

  public reorder(data) {
    return new ActivityReorderCommand(data).injectServices(this.serviceObj).prepareData();
  }

  public changeAttributePadding(data) {
    return new ActivityChangeAttributePaddingCommand(data).injectServices(this.serviceObj);
  }

  public createOrgModelNodesAttachment(
    data: OrgModelNodesAttachment[]
  ): ActivityAddOrgModelNodesAttachmentCommand {
    return new ActivityAddOrgModelNodesAttachmentCommand(data).injectServices(this.serviceObj);
  }

  public deleteOrgModelNodesAttachment(
    data: OrgModelNodesAttachment[]
  ): ActivityDeleteOrgModelNodesAttachmentCommand {
    return new ActivityDeleteOrgModelNodesAttachmentCommand(data).injectServices(this.serviceObj);
  }

  public removeLinkedDiagram(data) {
    return new ActivityRemoveLinkedDiagramCommand(data).injectServices(this.serviceObj);
  }

  public addLinkedDiagram(data) {
    return new ActivityAddLinkedDiagramCommand(data).injectServices(this.serviceObj);
  }

  public renumber(data) {
    return new ActivityRenumberCommand(data).injectServices(this.serviceObj).prepareData();
  }
}
